import { useMemo } from "react";
import dayjs from "dayjs";
import { COUNTRY_MAP, normalizeCountryCode } from "../../../../utils/Countries";
import { US_STATE_MAP, CANADA_PROVINCE_MAP } from "../../../../utils/States";
import { formatSectorLabel } from "../../../../constants/sectors";

/* ===============================
   Helpers
================================= */

function cleanDisplay(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  if (
    s.toLowerCase().startsWith("translation missing:") ||
    s.toLowerCase().startsWith("en.") ||
    s.toLowerCase().includes("unknown_")
  ) {
    return "";
  }
  return s;
}

function normalizeNaicsDigits(v = "") {
  return String(v).replace(/\D/g, "");
}

function normalizeFilterText(v = "") {
  return cleanDisplay(v).toLowerCase();
}

const GENERIC_SECTOR_TOKENS = new Set([
  "and",
  "business",
  "company",
  "companies",
  "industry",
  "industries",
  "sector",
  "sectors",
  "service",
  "services",
]);

function normalizeSectorForMatch(value = "") {
  return formatSectorLabel(value) || cleanDisplay(value);
}

function sectorTokens(value = "") {
  return normalizeSectorForMatch(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter((token) => token.length > 2 && !GENERIC_SECTOR_TOKENS.has(token));
}

function sectorMatchesTarget(candidate, target) {
  const candidateValue = normalizeSectorForMatch(candidate).toLowerCase();
  const targetValue = normalizeSectorForMatch(target).toLowerCase();

  if (!candidateValue || !targetValue) return false;
  if (candidateValue === targetValue) return true;
  if (candidateValue.includes(targetValue) || targetValue.includes(candidateValue)) {
    return true;
  }

  const candidateTokens = new Set(sectorTokens(candidate));
  return sectorTokens(target).some((token) => candidateTokens.has(token));
}

function getTargetIndustryBuckets(row = {}) {
  const buckets = [
    row.primary_naics_bucket,
    ...(Array.isArray(row.naics_buckets) ? row.naics_buckets : []),
  ];

  return buckets.map(normalizeFilterText).filter(Boolean);
}

function visitMatchesTargetCode(visitCode, targetCode) {
  const visitDigits = normalizeNaicsDigits(visitCode);
  const targetDigits = normalizeNaicsDigits(targetCode);

  return Boolean(
    visitDigits &&
      targetDigits &&
      (visitDigits.startsWith(targetDigits) ||
        targetDigits.startsWith(visitDigits)),
  );
}

function getNaicsFields(row = {}) {
  const naicsName =
    cleanDisplay(row.naics_name) ||
    cleanDisplay(row.primary_naics_bucket) ||
    cleanDisplay(row?.naics_extra?.new_title) ||
    cleanDisplay(row?.naics_extra?.name) ||
    cleanDisplay(row.naics_code) ||
    "";

  const naicsCode =
    cleanDisplay(row.naics_code) ||
    cleanDisplay(row?.naics_extra?.new_naicsprefixcode) ||
    cleanDisplay(row?.naics_extra?.new_code) ||
    cleanDisplay(row?.naics_extra?.new_naicscode) ||
    cleanDisplay(row?.naics_extra?.code) ||
    "";

  return { naicsName, naicsCode };
}

function getSector(row = {}) {
  return (
    cleanDisplay(row.sector) ||
    cleanDisplay(row?.naics_extra?.new_sector) ||
    cleanDisplay(row?.naics_extra?.sector) ||
    ""
  );
}

function getEffectiveSector(row = {}) {
  return formatSectorLabel(getSector(row));
}

function getVisitCompanyKey(row = {}) {
  const acct = row.visitor_account_id;
  if (acct) return `id:${acct}`;

  const name = cleanDisplay(row.visitor_company_name);
  const state = cleanDisplay(row.visitor_state);

  if (name && state) {
    return `name_state:${name.toLowerCase()}|${state.toLowerCase()}`;
  }

  if (name) return `name:${name.toLowerCase()}`;

  return row.id ? `row:${row.id}` : null;
}

/* =========================================================
   MAIN HOOK
========================================================= */

export default function useWebsiteVisitFilters({
  items,
  q,
  since,
  until,
  targetIndustryFilter,
  industryFilter,
  naicsFilter,
  stateFilter,
  countryFilter,
  clientTargetIndustries,
  clientTargetIndustryCodes,
  summary,
  burst,
  leadScoreSort,
}) {
  const companyTotals = useMemo(
    () => summary?.company_totals ?? {},
    [summary?.company_totals],
  );

  const companyTags = useMemo(
    () => burst?.company_tags ?? {},
    [burst?.company_tags],
  );

  const companyScores = useMemo(
    () => burst?.company_scores ?? {},
    [burst?.company_scores],
  );

  const allTargetIndustryCodes = useMemo(
    () => clientTargetIndustryCodes ?? [],
    [clientTargetIndustryCodes],
  );

  const allTargetIndustryLabels = useMemo(
    () =>
      (clientTargetIndustries ?? [])
        .map((industry) => industry?.label || industry?.name)
        .map((sector) => cleanDisplay(sector))
        .filter(Boolean),
    [clientTargetIndustries],
  );

  const filtered = useMemo(() => {
    const needle = q?.toLowerCase().trim() || "";
    const sinceDate = since ? new Date(since) : null;
    const untilDate = until ? new Date(until) : null;
    const groupedMap = new Map();

    for (const item of items || []) {
      const row = item?.representative || item;
      if (!row) continue;

      const visitDate = row.created_at ? new Date(row.created_at) : null;

      if (sinceDate && visitDate && visitDate < sinceDate) continue;
      if (untilDate && visitDate && visitDate > untilDate) continue;

      if (needle) {
        const { naicsName, naicsCode } = getNaicsFields(row);

        const haystack = [
          row.visitor_company_name,
          row.visitor_account_name,
          row.visitor_city,
          row.visitor_state,
          row.visitor_country,
          row.page_url,
          naicsName,
          naicsCode,
          row.primary_naics_bucket,
          getSector(row),
        ]
          .map((v) => cleanDisplay(v).toLowerCase())
          .join(" ");

        if (!haystack.includes(needle)) continue;
      }

      if (Array.isArray(targetIndustryFilter) && targetIndustryFilter.length) {
        const { naicsCode } = getNaicsFields(row);
        const targetBuckets = getTargetIndustryBuckets(row);
        const visitSector = getSector(row);

        const isTargetIndustry =
          (visitSector &&
            allTargetIndustryLabels.some((label) =>
              sectorMatchesTarget(visitSector, label),
            )) ||
          targetBuckets.some((bucket) =>
            allTargetIndustryLabels.some((label) =>
              sectorMatchesTarget(bucket, label),
            ),
          ) ||
          allTargetIndustryCodes.some((code) =>
            visitMatchesTargetCode(naicsCode, code),
          );

        const matchesTarget = targetIndustryFilter.some((selected) => {
          if (selected?.isAllTarget) return isTargetIndustry;

          const selectedLabel = cleanDisplay(selected?.label);
          if (selectedLabel && sectorMatchesTarget(visitSector, selectedLabel)) {
            return true;
          }

          if (
            selectedLabel &&
            targetBuckets.some((bucket) =>
              sectorMatchesTarget(bucket, selectedLabel),
            )
          ) {
            return true;
          }

          return visitMatchesTargetCode(naicsCode, selected?.code);
        });

        if (!matchesTarget) continue;
      }

      if (Array.isArray(industryFilter) && industryFilter.length) {
        const visitSector = getEffectiveSector(row);

        const matchesIndustry = industryFilter.some((selected) => {
          const selectedSector = formatSectorLabel(cleanDisplay(selected?.label));
          return selectedSector === visitSector;
        });

        if (!matchesIndustry) continue;
      }

      if (Array.isArray(naicsFilter) && naicsFilter.length) {
        const { naicsName } = getNaicsFields(row);
        if (!naicsFilter.includes(naicsName)) continue;
      }

      /* ===============================
         ✅ FIXED COUNTRY FILTER
      ================================= */
      if (Array.isArray(countryFilter) && countryFilter.length) {
        const rawCountry = cleanDisplay(row.visitor_country);

        const normalizedRow = normalizeCountryCode(rawCountry);
        const fullRowCountry =
          COUNTRY_MAP[normalizedRow] || rawCountry;

        const matchesCountry = countryFilter.some((selected) => {
          const normalizedSelected = normalizeCountryCode(selected);
          const fullSelected =
            COUNTRY_MAP[normalizedSelected] || selected;

          return (
            fullRowCountry === fullSelected ||
            normalizedRow === normalizedSelected
          );
        });

        if (!matchesCountry) continue;
      }

      /* ===============================
         STATE FILTER (unchanged)
      ================================= */
      if (Array.isArray(stateFilter) && stateFilter.length) {
        const rawState = cleanDisplay(row.visitor_state);
        const rawCountry = cleanDisplay(row.visitor_country);

        const normalizedCountry = normalizeCountryCode(rawCountry);
        const fullCountry = COUNTRY_MAP[normalizedCountry] || rawCountry;

        let fullState = rawState;

        if (fullCountry === "United States") {
          fullState = US_STATE_MAP[rawState] || rawState;
        }

        if (fullCountry === "Canada") {
          fullState = CANADA_PROVINCE_MAP[rawState] || rawState;
        }

        if (!stateFilter.includes(fullState)) continue;
      }

      const companyKey =
        item?.companyKey ||
        getVisitCompanyKey(row) ||
        (row.id ? `row:${row.id}` : null);

      if (!companyKey) continue;

      let group = groupedMap.get(companyKey);

      if (!group) {
        group = {
          representative: row,
          pages: item?.pages || [],
          companyKey,
          last_seen: row.last_seen || row.created_at || null,
          visits: item?.visits || [row],
          tags: item?.tags || [],
          user_tags: row.user_tags || [],
          auto_tags: row.auto_tags || [],
          leadScore:
            item?.leadScore ??
            (row?.lead_score != null ? Number(row.lead_score) || 0 : null),
          groupedVisitCount: item?.total_visits ?? null,
          sector: item?.sector || getSector(row),
        };
        groupedMap.set(companyKey, group);
      } else {
        const currentRep = group.representative;
        const currentRepDate = currentRep?.last_seen || currentRep?.created_at;
        const newRepDate = row.last_seen || row.created_at;
        const currentHasVisitId = Boolean(currentRep?.visit_id);
        const nextHasVisitId = Boolean(row?.visit_id);

        const hasBetterData =
          (!currentRep.visitor_account_id && row.visitor_account_id) ||
          (!currentRep.visitor_company_name && row.visitor_company_name) ||
          (!currentRep.visitor_city && row.visitor_city);

        if (
          (!currentHasVisitId && nextHasVisitId) ||
          hasBetterData ||
          (newRepDate &&
            currentRepDate &&
            dayjs(newRepDate).isAfter(dayjs(currentRepDate)))
        ) {
          group.representative = row;
          group.last_seen = newRepDate;
        }

        if (item?.visits?.length) {
          group.visits = [...group.visits, ...item.visits];
        } else {
          group.visits.push(row);
        }

        if (item?.pages?.length) {
          group.pages = [...group.pages, ...item.pages];
        } else {
          const pageUrl = cleanDisplay(row.page_url);
          if (pageUrl && !group.pages.find((p) => p.url === pageUrl)) {
            group.pages.push({
              url: pageUrl,
              last_seen: row.last_seen || row.created_at || null,
              duration_seconds:
                row.duration_seconds != null
                  ? row.duration_seconds
                  : row.duration ?? null,
            });
          }
        }

        if (item?.tags?.length) {
          group.tags = Array.from(
            new Set([...(group.tags || []), ...item.tags]),
          );
        }

        group.user_tags = row.user_tags || group.user_tags || [];
        group.auto_tags = row.auto_tags || group.auto_tags || [];

        const candidateLeadScore =
          item?.leadScore ??
          (row?.lead_score != null ? Number(row.lead_score) || 0 : null);

        if (candidateLeadScore != null) {
          group.leadScore =
            group.leadScore == null
              ? candidateLeadScore
              : Math.max(group.leadScore, candidateLeadScore);
        }

        group.sector = group.sector || item?.sector || getSector(row);
      }
    }

    return Array.from(groupedMap.values());
  }, [
    items,
    q,
    targetIndustryFilter,
    industryFilter,
    naicsFilter,
    stateFilter,
    countryFilter,
    allTargetIndustryCodes,
    allTargetIndustryLabels,
    since,
    until,
  ]);

  const preparedRows = useMemo(() => {
    return filtered.map((group) => {
      const row = group.representative;
      const key = group.companyKey ?? null;

      let durationSeconds =
        row.duration_seconds != null
          ? row.duration_seconds
          : row.duration ?? 0;

      durationSeconds = Math.max(0, Math.min(durationSeconds, 600));

      const visitDigits = normalizeNaicsDigits(getNaicsFields(row).naicsCode);
      const targetBuckets = getTargetIndustryBuckets(row);

      const isTargetIndustry =
        targetBuckets.some((bucket) =>
          allTargetIndustryLabels.includes(bucket),
        ) ||
        allTargetIndustryCodes.some((code) =>
          visitMatchesTargetCode(visitDigits, code),
        );

      const leadScore =
        group.leadScore ??
        (key && companyScores[key] != null
          ? Number(companyScores[key]) || 0
          : 0);

      const totalVisits =
        (group.groupedVisitCount ?? group.visits?.length) ||
        (key && companyTotals[key] != null ? companyTotals[key] : 0);

      const burstTags = key && companyTags[key] ? companyTags[key] : [];
      const autoTags = Array.from(
        new Set([...(group.auto_tags || []), ...(group.tags || []), ...burstTags]),
      );

      return {
        ...group,
        leadScore,
        durationSeconds,
        pagesCount: group.pages.length,
        isTargetIndustry,
        total_visits: totalVisits,
        auto_tags: autoTags,
        user_tags: group.user_tags || [],
        tags: isTargetIndustry ? ["🎯 Target Industry", ...autoTags] : autoTags,
        sector: group.sector || getSector(row),
      };
    });
  }, [
    filtered,
    allTargetIndustryCodes,
    allTargetIndustryLabels,
    companyTotals,
    companyTags,
    companyScores,
  ]);

  const sortedRows = useMemo(() => {
    if (!leadScoreSort) return preparedRows;

    const copy = [...preparedRows];

    copy.sort((a, b) => {
      const av = a.leadScore ?? -Infinity;
      const bv = b.leadScore ?? -Infinity;
      return leadScoreSort === "desc" ? bv - av : av - bv;
    });

    return copy;
  }, [preparedRows, leadScoreSort]);

  return { sortedRows };
}
